import { useState } from "react";
import { useLang } from "../LangContext";
import { supabase } from "../supabase";
import { ROLE_META, ROLE_LABELS } from "../constants";
import { formatEur, daysUntil } from "../utils";
import FloorPlan from "./FloorPlan";

export default function ClientTracking({ project, todos, journal, budget, materials, setTab, funnel }) {
  const [activePanel, setActivePanel] = useState("parcours");
  const [copied, setCopied] = useState(false);
  const { t, lang } = useLang();

  const allTodos = Object.values(todos || {}).flat();
  const myPending = allTodos.filter(t => t.assignee === "client" && !t.done);
  const myUrgent = myPending.filter(t => t.deadline && daysUntil(t.deadline) <= 7);
  const globalPct = funnel ? Math.round(funnel.reduce((s, st) => s + st.pct, 0) / funnel.length) : (allTodos.length ? Math.round((allTodos.filter(t => t.done).length / allTodos.length) * 100) : project.progress || 0);
  const totalEng = Object.values(materials || {}).flat().reduce((s, m) => s + (m.price || 0), 0);
  const validatedDecisions = (journal || []).filter(e => e.type === "formal_decision" && e.decisionStatus === "validated");

  // Build STAGES from funnel data if available, else use defaults
  const STAGES = funnel ? funnel.map((stage, i) => {
    const effectiveStatus = stage.pct === 100 ? "done" : stage.pct > 0 ? "active" : stage.status;
    return {
      label: stage.label,
      desc: i === 2 ? t("stage3Desc", { n: myPending.length, s: myPending.length > 1 ? "s" : "" }) : stage.desc,
      done: effectiveStatus === "done",
      active: effectiveStatus === "active",
      last: i === funnel.length - 1,
      date: effectiveStatus === "done" ? t("completed") : effectiveStatus === "active" ? t("inProgress") :
        (i === funnel.length - 1 && project.deadline) ? `${t("estimated")} · ${new Date(project.deadline).toLocaleDateString(lang === "en" ? "en-GB" : lang === "es" ? "es-ES" : "fr-FR", { month: "short", year: "numeric" })}` : t("upcoming"),
      pct: stage.pct,
    };
  }) : [
    { label: t("stage1Label"), done: true, desc: t("stage1Desc"), date: t("completed") },
    { label: t("stage2Label"), done: true, desc: t("stage2Desc"), date: t("completed") },
    { label: t("stage3Label"), active: true, desc: t("stage3Desc", { n: myPending.length, s: myPending.length > 1 ? "s" : "" }), date: t("inProgress") },
    { label: t("stage4Label"), done: false, desc: t("stage4Desc"), date: t("upcoming") },
    { label: t("stage5Label"), done: false, desc: t("stage5Desc"), date: t("upcoming") },
    { label: t("stage6Label"), done: false, last: true, desc: t("stage6Desc"), date: project.deadline ? `${t("estimated")} · ${new Date(project.deadline).toLocaleDateString(lang === "en" ? "en-GB" : lang === "es" ? "es-ES" : "fr-FR", { month: "short", year: "numeric" })}` : t("toSchedule") },
  ];

  return (
    <div style={{ maxWidth: 680, margin: "0 auto" }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 10, color: "#5A5450", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 6 }}>Votre projet · {project.address || t("addressNotSet")}</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <h1 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 28, fontWeight: 400, color: "#E8E2D8", marginBottom: 4 }}>{project.name}</h1>
            {project.join_code && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 10, color: "#5A5450", textTransform: "uppercase", letterSpacing: ".07em" }}>Code de partage</span>
                <span style={{ fontFamily: "monospace", fontSize: 12, color: "#C9A96E", letterSpacing: ".12em", background: "#252420", padding: "2px 8px", borderRadius: 5 }}>{project.join_code}</span>
                <button
                  onClick={() => { navigator.clipboard.writeText(project.join_code); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                  title="Copier le code"
                  style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 4px", color: copied ? "#7EB8C9" : "#5A5450", fontSize: 11, lineHeight: 1, transition: "color .2s" }}
                >
                  {copied ? "✓ Copié !" : "⧉"}
                </button>
              </div>
            )}
            <div style={{ fontSize: 13, color: "#5A5450" }}>Suivez votre rénovation en temps réel</div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontSize: 10, color: "#5A5450", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 }}>Avancement</div>
            <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 36, color: "#C9A96E", lineHeight: 1 }}>{globalPct}%</div>
          </div>
        </div>
        <div style={{ height: 4, background: "#252420", borderRadius: 99, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${globalPct}%`, background: "linear-gradient(90deg,#C9A96E,#E8C98A)", borderRadius: 99, transition: "width .8s ease" }} />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 20 }}>
        {[
          { label: t("delivery"), value: project.deadline ? new Date(project.deadline).toLocaleDateString(lang === "en" ? "en-GB" : lang === "es" ? "es-ES" : "fr-FR", { month: "short", year: "numeric" }) : t("toDefine"), color: "#C9A96E" },
          { label: t("daysLeft"), value: project.deadline ? `${Math.max(0, Math.ceil((new Date(project.deadline) - new Date()) / (1000 * 60 * 60 * 24)))}j` : "—", color: "#7EB8C9" },
          { label: t("myDecisions"), value: myUrgent.length > 0 ? `${myUrgent.length} ${t("urgent")}` : `${myPending.length} en attente`, color: myUrgent.length > 0 ? "#FC8181" : "#E8E2D8" },
          { label: t("engagedBudget"), value: formatEur(totalEng), color: "#E8E2D8" },
        ].map((s, i) => (
          <div key={i} className="card" style={{ padding: "12px 14px" }}>
            <div style={{ fontSize: 10, color: "#5A5450", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 14, fontWeight: 500, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {myUrgent.length > 0 && (
        <div onClick={() => setTab("todos")} style={{ background: "#2A1208", border: "1px solid #C9A96E40", borderRadius: 12, padding: "14px 18px", marginBottom: 20, cursor: "pointer", display: "flex", gap: 14, alignItems: "center" }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#FC818120", border: "1px solid #FC818140", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>⚠</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: "#FC8181", marginBottom: 3 }}>{myUrgent.length} décision{myUrgent.length > 1 ? "s" : ""} urgente{myUrgent.length > 1 ? "s" : ""} — délai projet à risque</div>
            <div style={{ fontSize: 12, color: "#7A5848" }}>{myUrgent[0]?.text}{myUrgent.length > 1 ? ` +${myUrgent.length - 1} autre${myUrgent.length > 2 ? "s" : ""}` : ""}</div>
          </div>
          <span style={{ fontSize: 12, color: "#C9A96E" }}>{t("seeMore")}</span>
        </div>
      )}

      <div style={{ display: "flex", gap: 4, background: "#1A1916", border: "1px solid #2A2924", borderRadius: 10, padding: 4, marginBottom: 20 }}>
        {[{ v: "parcours", l: t("myJourney") }, { v: "actions", l: t("myDecisions2") }, { v: "updates", l: t("updates") }].map(t => (
          <button key={t.v} onClick={() => setActivePanel(t.v)} className="btn"
            style={{ flex: 1, padding: "8px 4px", borderRadius: 7, fontSize: 12, fontWeight: activePanel === t.v ? 600 : 400, background: activePanel === t.v ? "#C9A96E20" : "none", color: activePanel === t.v ? "#C9A96E" : "#7A7468", border: activePanel === t.v ? "1px solid #C9A96E40" : "1px solid transparent" }}>
            {t.l}
          </button>
        ))}
      </div>

      {activePanel === "parcours" && (
        <>
          <div className="card" style={{ padding: 24 }}>
            <div style={{ position: "relative" }}>
              <div style={{ position: "absolute", left: 18, top: 0, bottom: 0, width: 1, background: "#2A2924" }} />
              <div style={{ position: "absolute", left: 18, top: 0, width: 1, height: "115px", background: "#C9A96E" }} />
              {STAGES.map((stage, i) => (
                <div key={i} style={{ display: "flex", gap: 16, paddingBottom: stage.last ? 0 : 22, position: "relative", zIndex: 1 }}>
                  <div style={{ width: 37, height: 37, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 500, background: stage.done ? "#C9A96E" : stage.active ? "#141410" : "#1A1916", border: stage.done ? "none" : stage.active ? "2px solid #C9A96E" : "1px solid #2A2924", color: stage.done ? "#0F0E0C" : stage.active ? "#C9A96E" : "#3A3830" }}>
                    {stage.done ? "✓" : stage.last ? "⬡" : i + 1}
                  </div>
                  <div style={{ paddingTop: 8, flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: stage.done || stage.active ? "#E8E2D8" : "#5A5450", marginBottom: 3 }}>{stage.label}</div>
                    <div style={{ fontSize: 12, color: stage.done || stage.active ? "#7A7468" : "#3A3830", lineHeight: 1.5, marginBottom: 5 }}>{stage.desc}</div>
                    <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, fontWeight: 500, background: stage.done ? "#C9A96E20" : stage.active ? "#7EB8C920" : "#1F1E1A", color: stage.done ? "#C9A96E" : stage.active ? "#7EB8C9" : "#3A3830" }}>{stage.date}</span>
                    {stage.pct !== undefined && (
                      <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ flex: 1, height: 3, background: "#252420", borderRadius: 99, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${stage.pct}%`, background: stage.done ? "#C9A96E" : stage.active ? "#7EB8C9" : "#3A3830", borderRadius: 99, transition: "width .5s ease" }} />
                        </div>
                        <span style={{ fontSize: 10, color: stage.done ? "#C9A96E" : stage.active ? "#7EB8C9" : "#3A3830", fontWeight: 600, minWidth: 28 }}>{stage.pct}%</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 11, color: "#7A7468", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>Plan du projet</div>
            <FloorPlan projectId={project.id} readOnly />
          </div>
        </>
      )}

      {activePanel === "actions" && (
        <div>
          {myPending.length === 0 ? (
            <div className="card" style={{ padding: 32, textAlign: "center" }}>
              <div style={{ fontSize: 28, marginBottom: 12 }}>✓</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: "#E8E2D8", marginBottom: 6 }}>Toutes vos décisions sont prises</div>
              <div style={{ fontSize: 12, color: "#5A5450" }}>Votre architecte vous notifiera des prochaines étapes</div>
            </div>
          ) : (
            <div className="card" style={{ padding: 8 }}>
              <div style={{ padding: "10px 14px 4px", fontSize: 12, color: "#5A5450" }}>Ces décisions sont nécessaires au démarrage des travaux.</div>
              {myPending.map(todo => {
                const days = todo.deadline ? daysUntil(todo.deadline) : null;
                const isUrgent = days !== null && days <= 7;
                const dlColor = days === null ? "#5A5450" : days < 0 ? "#FC8181" : days <= 3 ? "#FC8181" : days <= 7 ? "#F6AD55" : "#A8C97E";
                const dlLabel = days === null ? "" : days < 0 ? `${Math.abs(days)}${t("delay")}` : days === 0 ? t("today") : `J-${days}`;
                return (
                  <div key={todo.id} onClick={() => setTab("todos")} style={{ padding: "12px 14px", borderBottom: "1px solid #1F1E1A", cursor: "pointer" }}>
                    <div style={{ fontSize: 13, color: "#E8E2D8", marginBottom: 6 }}>{todo.text}</div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      {todo.deadline && <span style={{ fontSize: 11, fontWeight: 500, color: dlColor }}>📅 {dlLabel}</span>}
                      {todo.impact && isUrgent && <span style={{ fontSize: 11, color: "#7A6848" }}>⚠ {todo.impact}</span>}
                    </div>
                  </div>
                );
              })}
              <div style={{ padding: "12px 14px" }}>
                <button onClick={() => setTab("todos")} className="btn" style={{ width: "100%", padding: 10, background: "#C9A96E", color: "#0F0E0C", borderRadius: 8, fontSize: 13, fontWeight: 600, border: "none" }}>Voir toutes mes décisions →</button>
              </div>
            </div>
          )}
          {validatedDecisions.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 10, color: "#5A5450", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 10 }}>Décisions validées ({validatedDecisions.length})</div>
              <div className="card" style={{ padding: 8 }}>
                {validatedDecisions.map(e => (
                  <div key={e.id} style={{ display: "flex", gap: 10, padding: "10px 12px", borderBottom: "1px solid #1F1E1A", alignItems: "flex-start" }}>
                    <span style={{ fontSize: 14, flexShrink: 0 }}>{e.category === "material" ? "🧱" : "🏗"}</span>
                    <div style={{ flex: 1 }}><div style={{ fontSize: 12, color: "#E8E2D8", marginBottom: 2 }}>{e.text}</div><div style={{ fontSize: 10, color: "#5A5450" }}>{e.time}</div></div>
                    <span style={{ fontSize: 10, color: "#A8C97E", background: "#A8C97E20", padding: "2px 7px", borderRadius: 4, flexShrink: 0 }}>✓ Validée</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activePanel === "updates" && (
        <div className="card" style={{ padding: 8 }}>
          {(journal ?? []).length === 0 ? (
            <div style={{ padding: "32px 16px", textAlign: "center", fontSize: 12, color: "#5A5450" }}>Aucune mise à jour pour le moment</div>
          ) : (journal ?? []).slice(0, 10).map(e => {
            if (e.type === "email") {
              const rawSummary = typeof e.summary === "string" ? e.summary : JSON.stringify(e.summary ?? "");
              const cleaned = rawSummary.replace(/```json\s*/g, "").replace(/```\s*/g, "").trim();
              let summaryObj; try { summaryObj = JSON.parse(cleaned); } catch { summaryObj = { resume: cleaned }; }
              const resume = String(summaryObj.resume ?? "");
              const points = Array.isArray(summaryObj.points_cles) ? summaryObj.points_cles : [];
              const time = e.receivedAt
                ? new Date(e.receivedAt).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) + " · " + new Date(e.receivedAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
                : (e.time ?? "");
              return (
                <div key={e.id} style={{ padding: "12px 14px", borderBottom: "1px solid #1F1E1A", display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#7EB8C9", marginTop: 5, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11, color: "#7EB8C9", fontWeight: 500 }}>📧 Email</span>
                      <span style={{ fontSize: 11, color: "#7A7468" }}>{e.from}</span>
                      <span style={{ fontSize: 10, color: "#3A3830", marginLeft: "auto" }}>{time}</span>
                    </div>
                    <div style={{ fontSize: 12, color: "#7A6848", fontWeight: 500, marginBottom: (resume || points.length) ? 3 : 0 }}>{e.subject}</div>
                    {resume && <div style={{ fontSize: 13, color: "#B0AA9E", lineHeight: 1.5 }}>{resume}</div>}
                    {points.length > 0 && (
                      <ul style={{ margin: "5px 0 0", paddingLeft: 16 }}>
                        {points.map((pt, i) => <li key={i} style={{ fontSize: 12, color: "#7A7468", lineHeight: 1.6 }}>{String(pt)}</li>)}
                      </ul>
                    )}
                  </div>
                </div>
              );
            }
            const r = ROLE_META[e.role];
            const typeColor = e.type === "formal_decision" ? "#7EB8C9" : e.type === "document" ? "#A8C97E" : (r?.color ?? "#5A5450");
            return (
              <div key={e.id} style={{ padding: "12px 14px", borderBottom: "1px solid #1F1E1A", display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: typeColor, marginTop: 5, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, color: r?.color ?? "#5A5450", fontWeight: 500 }}>{r?.icon} {e.authorName || ROLE_LABELS[e.role]}</span>
                    {e.type === "formal_decision" && <span style={{ fontSize: 10, color: e.decisionStatus === "validated" ? "#A8C97E" : "#F6AD55", background: e.decisionStatus === "validated" ? "#A8C97E20" : "#F6AD5520", padding: "1px 6px", borderRadius: 4 }}>{e.decisionStatus === "validated" ? t("decisionValidated") : t("decisionPending")}</span>}
                    <span style={{ fontSize: 10, color: "#3A3830", marginLeft: "auto" }}>{e.time}</span>
                  </div>
                  <div style={{ fontSize: 13, color: "#B0AA9E", lineHeight: 1.5 }}>{e.text}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 20, padding: "16px 20px", background: "#141410", border: "1px solid #2A2924", borderRadius: 12, display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#C9A96E15", border: "1px solid #C9A96E30", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C9A96E" strokeWidth="1.5" strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 500, color: "#E8E2D8", marginBottom: 2 }}>
            {project.deadline ? `${t("estimatedDelivery")} — ${new Date(project.deadline).toLocaleDateString(lang === "en" ? "en-GB" : lang === "es" ? "es-ES" : "fr-FR", { month: "long", year: "numeric" })}` : t("deliveryToDefine")}
          </div>
          <div style={{ fontSize: 11, color: "#5A5450" }}>Se met à jour automatiquement selon l'avancement du projet</div>
        </div>
      </div>
    </div>
  );
}
